"use client";

import { useLayoutEffect, useRef, type ReactNode } from "react";

/** One reading/wide lane for the header and editor in normal, embedded and
 * shared pages. No transforms/containment: table fixed headers keep their
 * viewport containing block. Only structural resizing updates breakout math. */
export default function WorkspacePageLayout({ fullWidth = false, imageCover = false, nested = false, children }: {
  fullWidth?: boolean; imageCover?: boolean; nested?: boolean; children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const previousWidth = useRef(fullWidth);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || previousWidth.current === fullWidth) return;
    previousWidth.current = fullWidth;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    element.dataset.workspaceWidthMoving = "true";
    const timer = window.setTimeout(() => { delete element.dataset.workspaceWidthMoving; }, 260);
    return () => { window.clearTimeout(timer); delete element.dataset.workspaceWidthMoving; };
  }, [fullWidth]);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      const gutter = Number.parseFloat(getComputedStyle(element).paddingRight) || 0;
      // The editor has a symmetric gutter plus 4px ProseMirror padding.
      for (const [key, value] of Object.entries({
        "--workspace-page-half-width": `${width / 2}px`,
        "--workspace-page-grid-width": `${Math.max(0, width - gutter * 2 - 8)}px`,
        "--workspace-page-live-gutter": `${gutter}px`,
      })) if (element.style.getPropertyValue(key) !== value) element.style.setProperty(key, value);
    };
    measure();
    const observer = new ResizeObserver(measure); observer.observe(element);
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [fullWidth, nested]);
  if (nested) return <>{children}</>;
  return <div ref={ref} data-workspace-page-width={fullWidth ? "wide" : "reading"}
    className={`workspace-page-layout relative mx-auto ${fullWidth ? "max-w-full pr-6 sm:pr-10" : "max-w-[840px] pr-[68px] sm:pr-[72px]"} ${imageCover ? "mt-[112px]" : ""}`}>
    {children}
  </div>;
}
